import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCalculator, FaBook, FaLanguage, FaChevronRight, FaArrowRight, FaClock, FaQuestionCircle } from 'react-icons/fa';
import './QuizInterface.css';

const QuizInterface = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedSubTopic, setSelectedSubTopic] = useState(null);
    const [animateCards, setAnimateCards] = useState(false);

    useEffect(() => {
        // Handle pre-selected subject and topic from navigation
        if (location.state?.subject) {
            setSelectedSubject(location.state.subject);
            if (location.state?.grade) {
                setSelectedGrade(location.state.grade);
                if (location.state?.topic) {
                    setSelectedTopic(location.state.topic);
                    // Only set subtopic if it's explicitly provided in the navigation state
                    if (location.state?.subtopic) {
                        setSelectedSubTopic(location.state.subtopic);
                    }
                }
            }
        }
        setTimeout(() => setAnimateCards(true), 100);
    }, [location.state]);

    const subjects = [
        { 
            id: 'math', 
            name: 'Mathematics', 
            icon: <FaCalculator />, 
            color: '#4f46e5',
            gradient: 'linear-gradient(135deg, #4f46e5, #818cf8)',
            description: 'Master mathematical concepts and problem-solving skills'
        },
        { 
            id: 'english', 
            name: 'English', 
            icon: <FaBook />, 
            color: '#0891b2',
            gradient: 'linear-gradient(135deg, #0891b2, #22d3ee)',
            description: 'Enhance your English grammar and vocabulary'
        },
        { 
            id: 'russian', 
            name: 'Russian', 
            icon: <FaLanguage />, 
            color: '#e11d48',
            gradient: 'linear-gradient(135deg, #e11d48, #fb7185)',
            description: 'Improve your Russian language proficiency'
        }
    ];

    const grades = [
        { id: 9, name: '9th Grade', description: 'Intermediate level concepts' },
        { id: 10, name: '10th Grade', description: 'Advanced level concepts' },
        { id: 11, name: '11th Grade', description: 'Expert level concepts' }
    ];

    const topics = {
        math: {
            9: [
                {
                    id: 'real_numbers',
                    name: 'Real Numbers',
                    description: 'Understanding real numbers and their properties',
                    subtopics: [
                        { id: 'rational_numbers', name: 'Rational Numbers', description: 'Properties and operations with rational numbers', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'irrational_numbers', name: 'Irrational Numbers', description: 'Understanding and working with irrational numbers', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'number_modulus', name: 'Modulus of a Number', description: 'Absolute value and its applications', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                },
                {
                    id: 'algebra',
                    name: 'Algebra',
                    description: 'Basic algebraic concepts and operations',
                    subtopics: [
                        { id: 'linear_equations', name: 'Linear Equations', description: 'Solving linear equations and inequalities', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'quadratic_equations', name: 'Quadratic Equations', description: 'Solving quadratic equations and their applications', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'polynomials', name: 'Polynomials', description: 'Operations and factorization of polynomials', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                },
                {
                    id: 'geometry',
                    name: 'Geometry',
                    description: 'Basic geometric shapes and their properties',
                    subtopics: [
                        { id: 'triangles', name: 'Triangles', description: 'Properties and congruence of triangles', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'circles', name: 'Circles', description: 'Circle properties and theorems', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            10: [
                {
                    id: 'functions',
                    name: 'Functions',
                    description: 'Understanding functions and their properties',
                    subtopics: [
                        { id: 'linear_functions', name: 'Linear Functions', description: 'Properties and graphs of linear functions', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'quadratic_functions', name: 'Quadratic Functions', description: 'Properties and graphs of quadratic functions', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'exponential_functions', name: 'Exponential Functions', description: 'Understanding exponential growth and decay', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                },
                {
                    id: 'trigonometry',
                    name: 'Trigonometry',
                    description: 'Trigonometric functions and identities',
                    subtopics: [
                        { id: 'basic_trigonometry', name: 'Basic Trigonometry', description: 'Sine, cosine and tangent relationships', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'trig_identities', name: 'Trigonometric Identities', description: 'Fundamental trigonometric identities', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            11: [
                {
                    id: 'calculus',
                    name: 'Calculus',
                    description: 'Introduction to calculus concepts',
                    subtopics: [
                        { id: 'derivatives', name: 'Derivatives', description: 'Understanding and calculating derivatives', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'integrals', name: 'Integrals', description: 'Understanding and calculating integrals', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'limits', name: 'Limits', description: 'Concept of limits and continuity', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                },
                {
                    id: 'probability',
                    name: 'Probability & Statistics',
                    description: 'Basic probability theory and statistical measures',
                    subtopics: [
                        { id: 'basic_probability', name: 'Basic Probability', description: 'Probability rules and calculations', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'distributions', name: 'Distributions', description: 'Normal and binomial distributions', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ]
        },
        english: {
            9: [
                {
                    id: 'grammar',
                    name: 'Grammar',
                    description: 'Basic English grammar concepts',
                    subtopics: [
                        { id: 'tenses', name: 'Tenses', description: 'Understanding and using different tenses', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'parts_of_speech', name: 'Parts of Speech', description: 'Noun, verb, adjective identification', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            10: [
                {
                    id: 'literature',
                    name: 'Literature',
                    description: 'Introduction to literary analysis',
                    subtopics: [
                        { id: 'poetry', name: 'Poetry Analysis', description: 'Analyzing poetic devices and structures', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'prose', name: 'Prose Analysis', description: 'Understanding narrative techniques', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            11: [
                {
                    id: 'writing',
                    name: 'Writing Skills',
                    description: 'Advanced composition techniques',
                    subtopics: [
                        { id: 'essay_writing', name: 'Essay Writing', description: 'Structuring academic essays', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'creative_writing', name: 'Creative Writing', description: 'Developing narrative skills', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ]
        },
        russian: {
            9: [
                {
                    id: 'grammar',
                    name: 'Grammar',
                    description: 'Basic Russian grammar concepts',
                    subtopics: [
                        { id: 'cases', name: 'Cases', description: 'Understanding and using Russian cases', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'verbs', name: 'Verb Conjugation', description: 'Regular and irregular verb forms', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            10: [
                {
                    id: 'literature',
                    name: 'Literature',
                    description: 'Classic Russian literature',
                    subtopics: [
                        { id: 'golden_age', name: 'Golden Age Literature', description: '19th century authors and works', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'poetry', name: 'Russian Poetry', description: 'Analysis of poetic works', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ],
            11: [
                {
                    id: 'advanced_grammar',
                    name: 'Advanced Grammar',
                    description: 'Complex grammatical structures',
                    subtopics: [
                        { id: 'participles', name: 'Participles', description: 'Formation and usage of participles', questionCount: 5, timeLimit: '10 minutes' },
                        { id: 'syntax', name: 'Syntax', description: 'Sentence structure analysis', questionCount: 5, timeLimit: '10 minutes' }
                    ]
                }
            ]
        }
    };

    const handleStartQuiz = () => {
        navigate('/quiz/session', {
            state: {
                subject: selectedSubject,
                grade: selectedGrade,
                topic: selectedTopic,
                subtopic: selectedSubTopic
            }
        });
    };

    const resetSelections = () => {
        setAnimateCards(false);
        setTimeout(() => {
            setSelectedSubject(null);
            setSelectedGrade(null);
            setSelectedTopic(null);
            setSelectedSubTopic(null);
            setAnimateCards(true);
        }, 300);
    };

    const handleSubjectSelect = (subjectId) => {
        setSelectedSubject(subjectId);
        setSelectedGrade(null);
        setSelectedTopic(null);
        setSelectedSubTopic(null);
    };

    const handleGradeSelect = (gradeId) => {
        setSelectedGrade(gradeId);
        setSelectedTopic(null);
        setSelectedSubTopic(null);
    };

    const handleTopicSelect = (topicId) => {
        setSelectedTopic(topicId);
        setSelectedSubTopic(null);
    };

    return (
        <div className="quiz-interface">
            <div className="quiz-header">
                <h1>Start Your Learning Journey</h1>
                
                {(selectedSubject || selectedGrade || selectedTopic || selectedSubTopic) && (
                    <div className="selection-path">
                        <div className="selection-buttons">
                            <button onClick={resetSelections} className="reset-selection">
                                Start Over
                            </button>
                        </div>
                        
                        <div className="selection-breadcrumbs">
                            {selectedSubject && (
                                <span 
                                    className="breadcrumb-item clickable"
                                    onClick={() => {
                                        setSelectedGrade(null);
                                        setSelectedTopic(null);
                                        setSelectedSubTopic(null);
                                    }}
                                >
                                    {subjects.find(s => s.id === selectedSubject)?.name}
                                </span>
                            )}
                            
                            {selectedGrade && (
                                <>
                                    <span className="breadcrumb-separator"><FaChevronRight /></span>
                                    <span 
                                        className="breadcrumb-item clickable"
                                        onClick={() => {
                                            setSelectedTopic(null);
                                            setSelectedSubTopic(null);
                                        }}
                                    >
                                        {grades.find(g => g.id === selectedGrade)?.name}
                                    </span>
                                </>
                            )}
                            
                            {selectedTopic && (
                                <>
                                    <span className="breadcrumb-separator"><FaChevronRight /></span>
                                    <span 
                                        className="breadcrumb-item clickable"
                                        onClick={() => setSelectedSubTopic(null)}
                                    >
                                        {topics[selectedSubject]?.[selectedGrade]?.find(t => t.id === selectedTopic)?.name}
                                    </span>
                                </>
                            )}

                            {selectedSubTopic && (
                                <>
                                    <span className="breadcrumb-separator"><FaChevronRight /></span>
                                    <span className="breadcrumb-item">
                                        {topics[selectedSubject]?.[selectedGrade]
                                            ?.find(t => t.id === selectedTopic)
                                            ?.subtopics?.find(st => st.id === selectedSubTopic)?.name}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={`quiz-selection ${animateCards ? 'animate' : ''}`}>
                {/* Subject Selection */}
                {!selectedSubject && (
                    <div className="selection-section">
                        <h2>Select Subject</h2>
                        <div className="selection-grid">
                            {subjects.map((subject, index) => (
                                <button
                                    key={subject.id}
                                    className="subject-card"
                                    style={{ 
                                        background: subject.gradient,
                                        animationDelay: `${index * 0.1}s`
                                    }}
                                    onClick={() => handleSubjectSelect(subject.id)}
                                >
                                    <div className="subject-icon">{subject.icon}</div>
                                    <h3>{subject.name}</h3>
                                    <p className="subject-description">{subject.description}</p>
                                    <div className="card-overlay">
                                        <span className="select-btn">Select <FaArrowRight /></span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grade Selection */}
                {selectedSubject && !selectedGrade && (
                    <div className="selection-section">
                        <h2>Select Grade Level</h2>
                        <div className="selection-grid grade-cards">
                            {grades.map((grade, index) => (
                                <button
                                    key={grade.id}
                                    className="grade-card"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    onClick={() => handleGradeSelect(grade.id)}
                                >
                                    <div className="grade-number">{grade.id}</div>
                                    <h3>{grade.name}</h3>
                                    <p className="grade-description">{grade.description}</p>
                                    <div className="card-overlay">
                                        <span className="select-btn">Select <FaArrowRight /></span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Topic Selection */}
                {selectedSubject && selectedGrade && !selectedTopic && (
                    <div className="selection-section">
                        <h2>Select Topic</h2>
                        <div className="selection-grid">
                            {topics[selectedSubject]?.[selectedGrade]?.map((topic, index) => (
                                <button
                                    key={topic.id}
                                    className="topic-card"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    onClick={() => handleTopicSelect(topic.id)}
                                >
                                    <h3>{topic.name}</h3>
                                    <p className="topic-description">{topic.description}</p>
                                    <div className="topic-meta">
                                        <span className="meta-item">
                                            <FaQuestionCircle /> {topic.subtopics.length} Subtopics
                                        </span>
                                    </div>
                                    <div className="card-overlay">
                                        <span className="select-btn">Select <FaArrowRight /></span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Subtopic Selection */}
                {selectedSubject && selectedGrade && selectedTopic && !selectedSubTopic && (
                    <div className="selection-section">
                        <h2>Select Subtopic</h2>
                        <div className="selection-grid">
                            {topics[selectedSubject]?.[selectedGrade]
                                ?.find(t => t.id === selectedTopic)
                                ?.subtopics?.map((subtopic, index) => (
                                    <button
                                        key={subtopic.id}
                                        className="subtopic-card"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                        onClick={() => setSelectedSubTopic(subtopic.id)}
                                    >
                                        <h3>{subtopic.name}</h3>
                                        <p className="subtopic-description">{subtopic.description}</p>
                                        <div className="subtopic-meta">
                                            <span className="meta-item">
                                                <FaQuestionCircle /> {subtopic.questionCount} Questions
                                            </span>
                                            <span className="meta-item">
                                                <FaClock /> {subtopic.timeLimit}
                                            </span>
                                        </div>
                                        <div className="card-overlay">
                                            <span className="select-btn">Select <FaArrowRight /></span>
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>
                )}

                {/* Quiz Ready */}
                {selectedSubject && selectedGrade && selectedTopic && selectedSubTopic && (
                    <div className="quiz-ready-container">
                        <div className="quiz-ready-card">
                            <div className="quiz-ready-icon">
                                {subjects.find(s => s.id === selectedSubject)?.icon}
                            </div>
                            <h2>You're all set!</h2>
                            <div className="quiz-details">
                                <p><strong>Subject:</strong> {subjects.find(s => s.id === selectedSubject)?.name}</p>
                                <p><strong>Grade:</strong> {grades.find(g => g.id === selectedGrade)?.name}</p>
                                <p><strong>Topic:</strong> {topics[selectedSubject]?.[selectedGrade]?.find(t => t.id === selectedTopic)?.name}</p>
                                <p><strong>Subtopic:</strong> {topics[selectedSubject]?.[selectedGrade]?.find(t => t.id === selectedTopic)?.subtopics?.find(st => st.id === selectedSubTopic)?.name}</p>
                            </div>
                            <p className="quiz-description">
                                You'll have {topics[selectedSubject]?.[selectedGrade]?.find(t => t.id === selectedTopic)?.subtopics?.find(st => st.id === selectedSubTopic)?.timeLimit} to complete the quiz.
                                Get ready to put your knowledge to the test!
                            </p>
                            <button className="start-quiz-button" onClick={handleStartQuiz}>
                                Start Quiz Now
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizInterface; 